import { autoinject } from "aurelia-dependency-injection";
import { RequestClientService } from "@rightship-tech/common-ui/services/basic/request-client.service";
import {
    Dashboard, PersistDashboardRequest, PersistDashboardResponse, GetWidgetsResponse,
    GetDashboardsResponse, PersistDashboardsResponse, PersistDashboardsRequest, DeleteDashboardResponse,
    DeleteDashboardRequest, ShareDashboardRequest, UpdateDashboardWidgetRequest, UpdateDashboardWidgetResponse, GetDashboardAssignmentsRequest, DeleteDashboardAssignmentsRequest, AddDashboardAssignmentsRequest, AddDashboardAssignmentsResponse, DeleteDashboardAssignmentsResponse, GetDashboardAssignmentsResponse, SingleDashboardAssignment, CloneDashboardWidgetResponse, CloneDashboardWidgetRequest, GetDashboardWidgetsResponse, GetWidgetsForDashboardRequest, DashboardWidgetCoordinates, SaveDashboardAssignmentsRequest, SaveDashboardAssignmentsResponse, DashboardWidget
} from '../../models/dashboard/dashboard.models';

@autoinject
export class DashboardClientService {

    constructor(private requestClient: RequestClientService) {
    }

    public getDashboardsForUser(): Promise<GetDashboardsResponse> {
        const request = {};
        return this.requestClient.get<GetDashboardsResponse>("dashboard/GetDashboardsForUser", request);
    }

    public getGlobalDashboards(): Promise<GetDashboardsResponse> {
        const request = {};
        return this.requestClient.get<GetDashboardsResponse>("dashboard/GetGlobalDashboards", request);
    }

    public getGlobalDashboardsForUser(): Promise<GetDashboardsResponse> {
        const request = {};
        return this.requestClient.get<GetDashboardsResponse>("dashboard/GetGlobalDashboardsForUser", request);
    }

    public saveDashboardAssignments(request: SaveDashboardAssignmentsRequest): Promise<SaveDashboardAssignmentsResponse> {
        return this.requestClient.post<SaveDashboardAssignmentsResponse>("dashboard/SaveDashboardAssignments", {}, request);
    }

    public addDashboardAssignments(request: AddDashboardAssignmentsRequest): Promise<AddDashboardAssignmentsResponse> {
        return this.requestClient.post<AddDashboardAssignmentsResponse>("dashboard/AddDashboardAssignments", {}, request);
    }

    public deleteDashboardAssignments(request: DeleteDashboardAssignmentsRequest): Promise<DeleteDashboardAssignmentsResponse> {
        return this.requestClient.delete<DeleteDashboardAssignmentsResponse>("dashboard/DeleteDashboardAssignments", {}, request);
    }

    public getDashboardAssignments(request: GetDashboardAssignmentsRequest): Promise<GetDashboardAssignmentsResponse> {
        return this.requestClient.get<GetDashboardAssignmentsResponse>("dashboard/GetDashboardAssignments", request);
    }

    public getWidgets(): Promise<GetWidgetsResponse> {
        const request = {};
        return this.requestClient.get<GetWidgetsResponse>("dashboard/Widgets", request);
    }

    public getWidgetsForDashboard(dashboardId: number): Promise<GetDashboardWidgetsResponse> {
        const request: GetWidgetsForDashboardRequest = {
            dashboardId: dashboardId
        };

        return this.requestClient.get<GetDashboardWidgetsResponse>("dashboard/DashboardWidgets", request);
    }

    public persistDashboard(dashboard: Dashboard, widgets: DashboardWidget[], atRoleLevel: boolean = null, skipUserAssociation: boolean = false, isGlobalChosen: boolean = false): Promise<PersistDashboardResponse> {
        const request: PersistDashboardRequest = {
            dashboard: dashboard,
            atRoleLevel: atRoleLevel,
            widgets: widgets,
            skipUserAssociation: skipUserAssociation,
            isGlobalChosen: isGlobalChosen
        };
        return this.requestClient.post<PersistDashboardResponse>("dashboard/PersistDashboard", {}, request);
    }

    public persistDashboardMetaData(dashboard: Dashboard, widgets: DashboardWidget[]): Promise<PersistDashboardResponse> {
        const request: PersistDashboardRequest = {
            dashboard: dashboard,
            atRoleLevel: null,
            widgets: widgets,
            skipUserAssociation: false,
            isGlobalChosen: null
        };
        return this.requestClient.post<PersistDashboardResponse>("dashboard/SaveDashboardMeta", {}, request);
    }

    public cloneDashboard(dashboard: Dashboard): Promise<PersistDashboardResponse> {
        const request: PersistDashboardRequest = {
            dashboard: dashboard,
            widgets: null,
            atRoleLevel: null,
            skipUserAssociation: false,
            isGlobalChosen: null
        };
        return this.requestClient.post<PersistDashboardResponse>("dashboard/CloneDashboard", {}, request);
    }

    public cloneDashboardWidget(dashboardWidgetId: number, coords: DashboardWidgetCoordinates, dashboardId?: number): Promise<CloneDashboardWidgetResponse> {
        const request: CloneDashboardWidgetRequest = {
            dashboardWidgetId: dashboardWidgetId,
            dashboardId: dashboardId,
            coordinates: coords
        }

        return this.requestClient.post<CloneDashboardWidgetResponse>("dashboard/CloneDashboardWidget", {}, request);
    }

    public persistDashboards(dashboards: Dashboard[], dashboardRoleLinks: { [dashboardId: number]: boolean }): Promise<PersistDashboardsResponse> {
        const request: PersistDashboardsRequest = {
            dashboards: dashboards,
            dashboardRoleLinks: dashboardRoleLinks
        };
        return this.requestClient.post<PersistDashboardsResponse>("dashboard/updatedashboards", {}, request);
    }

    public deleteDashboard(dashboardId: number, deleteFully?: boolean): Promise<DeleteDashboardResponse> {
        const request: DeleteDashboardRequest = {
            dashboardId: dashboardId,
            deleteFully: deleteFully
        };

        return this.requestClient.post<DeleteDashboardResponse>("dashboard/DeleteDashboard", {}, request);
    }

    public deleteUserDashboard(dashboardId: number): Promise<DeleteDashboardResponse> {
        const request: DeleteDashboardRequest = {
            dashboardId: dashboardId
        };
        return this.requestClient.post<DeleteDashboardResponse>("dashboard/DeleteUserDashboard", {}, request);
    }

    public shareDashboard(assignment: SingleDashboardAssignment, readOnly: boolean): Promise<boolean> {
        return this.requestClient.post<boolean>("dashboard/Share", {}, <ShareDashboardRequest>{ assignment: assignment, shareAsReadOnly: readOnly });
    }

    public updateDashboardWidget(dashboardWidgetId: number, settings: string): Promise<UpdateDashboardWidgetResponse> {
        const request: UpdateDashboardWidgetRequest = {
            dashboardWidgetId: dashboardWidgetId,
            settings: settings
        };
        return this.requestClient.post<UpdateDashboardWidgetResponse>("dashboard/UpdateDashboardWidget", {}, request);
    }
}
